import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { Empty, ErrorMsg, Loading, PageHead, Section } from '../../components/ui';
import { api, errorText } from '../../lib/api';
import type { Asset, ItemRow, Permission, Project } from '../../lib/contentTypes';
import { AssetCard, ContentStatusBadge, FORMAT_LABEL, uploadAssets } from './common';
import { NewItemDialog, UsageSelect } from './ContentHome';
import { ProjectPublications } from './Publications';
import { ProjectVideos } from './Videos';

type Detail = Project & { assets: Asset[]; content_items: ItemRow[] };
const PERMS: Record<Permission, string> = {
  unknown: 'not decided',
  not_allowed: 'not allowed',
  allowed: 'allowed, client may be named',
  allowed_anonymous: 'allowed without naming the client',
};

const lines = (s: string) => s.split('\n').map((x) => x.trim()).filter(Boolean);

export function ProjectPage() {
  const { id } = useParams();
  const [p, setP] = useState<Detail | null>(null);
  const [f, setF] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [newItem, setNewItem] = useState(false);
  const load = useCallback(() => {
    api<Detail>(`/api/content/projects/${id}`)
      .then((d) => {
        setP(d);
        setF({
          title: d.title,
          location: d.location ?? '',
          client_name: d.client_name ?? '',
          public_description: d.public_description ?? '',
          internal_notes: d.internal_notes ?? '',
          services: d.services.join('\n'),
          confirmed_facts: d.confirmed_facts.join('\n'),
          credits: d.credits ?? '',
          language: d.language,
          publication_permission: d.publication_permission,
          embargo_until: d.embargo_until ? d.embargo_until.slice(0, 10) : '',
          tags: d.tags.join(', '),
        });
      })
      .catch((e) => setError(errorText(e)));
  }, [id]);
  useEffect(load, [load]);
  if (error && !p) return <ErrorMsg>{error}</ErrorMsg>;
  if (!p) return <Loading what="Loading the project" />;

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setMsg('');
    try {
      await api(`/api/content/projects/${id}`, {
        method: 'PATCH',
        body: {
          title: f.title,
          location: f.location || null,
          client_name: f.client_name || null,
          public_description: f.public_description || null,
          internal_notes: f.internal_notes || null,
          services: lines(f.services),
          confirmed_facts: lines(f.confirmed_facts),
          credits: f.credits || null,
          language: f.language || 'en',
          publication_permission: f.publication_permission,
          embargo_until: f.embargo_until ? `${f.embargo_until}T00:00:00Z` : null,
          tags: f.tags.split(',').map((t) => t.trim()).filter(Boolean),
          version: p!.version,
        },
      });
      setMsg('Project saved.');
      load();
    } catch (err) {
      setError(errorText(err));
    }
  }
  async function onFiles(files: FileList | null) {
    if (!files?.length) return;
    const { done, errors } = await uploadAssets(Array.from(files), { projectId: p!.id, onProgress: setMsg });
    setMsg(`${done.length} image${done.length === 1 ? '' : 's'} uploaded${errors.length ? `; refused: ${errors.join('; ')}` : ''}`);
    // an image already stored elsewhere is the same asset: attach it to this project
    for (const a of done.filter((x) => !x.created && x.project_id !== p!.id)) {
      await api(`/api/content/assets/${a.id}`, { method: 'PATCH', body: { project_id: p!.id } });
    }
    load();
  }
  return (
    <div className="page">
      <p className="crumbs small">
        <Link to="/content?tab=projects">Content Studio · Projects</Link>
      </p>
      <PageHead title={p.title} meta="Project">
        <button type="button" className="btn primary" onClick={() => setNewItem(true)}>
          New content item from this project
        </button>
      </PageHead>
      <form className="form-grid" onSubmit={save} aria-label="Project">
        <label className="field">
          <span>Title (public)</span>
          <input value={f.title} onChange={set('title')} required />
        </label>
        <label className="field">
          <span>Location (public)</span>
          <input value={f.location} onChange={set('location')} />
        </label>
        <label className="field span2">
          <span>Approved public description (templates may use it)</span>
          <textarea rows={3} value={f.public_description} onChange={set('public_description')} />
        </label>
        <label className="field">
          <span>Client (internal, never filled into a template)</span>
          <input value={f.client_name} onChange={set('client_name')} />
        </label>
        <label className="field">
          <span>Publication permission</span>
          <select value={f.publication_permission} onChange={set('publication_permission')}>
            {(Object.keys(PERMS) as Permission[]).map((k) => (
              <option key={k} value={k}>
                {PERMS[k]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Embargo until (no “published” before this date)</span>
          <input type="date" value={f.embargo_until} onChange={set('embargo_until')} />
        </label>
        <label className="field">
          <span>Content language</span>
          <select value={f.language} onChange={set('language')}>
            <option value="en">English</option>
            <option value="fr">French</option>
            <option value="bg">Bulgarian</option>
          </select>
        </label>
        <label className="field">
          <span>Services (one per line)</span>
          <textarea rows={3} value={f.services} onChange={set('services')} />
        </label>
        <label className="field">
          <span>Confirmed facts (one per line; only what is true and approved)</span>
          <textarea rows={3} value={f.confirmed_facts} onChange={set('confirmed_facts')} />
        </label>
        <label className="field">
          <span>Credits</span>
          <input value={f.credits} onChange={set('credits')} />
        </label>
        <label className="field">
          <span>Tags (comma separated)</span>
          <input value={f.tags} onChange={set('tags')} />
        </label>
        <label className="field span2">
          <span>Internal notes (never in a template or an export)</span>
          <textarea rows={2} value={f.internal_notes} onChange={set('internal_notes')} />
        </label>
        <div className="span2 row gap center">
          <button type="submit" className="btn primary">
            Save project
          </button>
          {msg && <span className="ok-text small">{msg}</span>}
        </div>
        <div className="span2">
          <ErrorMsg>{error}</ErrorMsg>
        </div>
      </form>
      <Section
        title={`Images (${p.assets.length})`}
        aside={
          <label className="btn file-btn small">
            Upload images
            <input type="file" accept="image/jpeg,image/png" multiple onChange={(e) => void onFiles(e.target.files)} aria-label="Upload project images" />
          </label>
        }
      >
        {p.assets.length === 0 ? (
          <Empty>No images yet. Originals are kept byte-for-byte; the editor crops and frames them without changing the file.</Empty>
        ) : (
          <div className="cs-assets">
            {p.assets.map((a) => (
              <AssetCard key={a.id} asset={a}>
                <UsageSelect asset={a} onChange={load} />
                <a className="small" href={a.urls.original}>
                  Original
                </a>
              </AssetCard>
            ))}
          </div>
        )}
      </Section>
      <ProjectPublications projectId={p.id} />
      <ProjectVideos projectId={p.id} />
      <Section title={`Content items (${p.content_items.length})`}>
        {p.content_items.length === 0 ? (
          <Empty>No content made from this project yet.</Empty>
        ) : (
          <ul className="plain cs-projects">
            {p.content_items.map((i) => (
              <li key={i.id} className="row gap center wrap">
                <Link to={`/content/items/${i.id}`}>{i.title}</Link>
                <span className="muted small">
                  {FORMAT_LABEL[i.format]} {i.width}×{i.height}
                </span>
                <ContentStatusBadge status={i.status} />
              </li>
            ))}
          </ul>
        )}
      </Section>
      {newItem && <NewItemDialog projectId={p.id} onClose={() => setNewItem(false)} />}
    </div>
  );
}
